"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { login } from "@/app/actions"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Link, useRouter } from "@/i18n/navigation"
import { cn } from "@/lib/utils"
import { RoleQueryOptions } from "@/queries/role.factory"
import { useAuthStore } from "@/stores/auth-store"

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
})

type LoginCredentials = z.infer<typeof loginSchema>

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter()
  const t = useTranslations("login")
  const queryClient = useQueryClient()
  const { login: storeLogin, setIsAdmin } = useAuthStore()
  const form = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const res = await login(credentials)
      return res
    },
    onSuccess: async (response) => {
      if ("error" in response) {
        toast.error(response.error)
        return
      }

      storeLogin({
        id: response.value.userId,
        name: response.value.name,
        username: response.value.username,
        isAdmin: false,
        avatar: "/WIN2TEC-GMS.png",
        email: `${response.value.username}@win2tec.com`,
        accessToken: response.value.accessToken,
        roleId: response.value.roleId,
      })
      queryClient.clear()
      // we need to set the admin apart, because the we set the token first during the login, and  if
      // we fetch the roles without the token, it will throw a 401 error, generating a loop
      try {
        const roles = await queryClient.fetchQuery(RoleQueryOptions.all)
        const userRole = roles.find(role => role.roleId === response.value.roleId)
        setIsAdmin(userRole?.code === "ADMIN")
      }
      catch (error) {
        console.log("Error fetching role information:", error)
      }
      router.push("/")
    },
    onError: (error) => {
      console.error("Login failed:", error)
      if (error instanceof Error && error.message) {
        toast.error(error.message)
      }
      else {
        toast.error("Login failed")
      }
    },
  })

  const handleSubmit = async (values: LoginCredentials) => {
    loginMutation.mutate(values)
  }

  return (
    <div className={cn("flex min-h-screen items-center justify-center bg-background", className)} {...props}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("title")}</CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="space-y-6" onSubmit={ev => void form.handleSubmit(handleSubmit)(ev)}>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("username")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("usernamePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t("password")}</FormLabel>
                      <a
                        className="text-sm text-primary underline-offset-4 hover:underline"
                        href="#"
                      >
                        {t("forgotPassword")}
                      </a>
                    </div>
                    <FormControl>
                      <Input
                        placeholder={t("passwordPlaceholder")}
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full"
                  disabled={loginMutation.isPending}
                  type="submit"
                >
                  {loginMutation.isPending ? t("loggingIn") : t("login")}
                </Button>
              </div>
              <div className="text-center text-sm">
                {t("noAccount")}
                {" "}
                <Link className="text-primary underline-offset-4 hover:underline" href="#">
                  {t("signUp")}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
